export type LogGrepFilter = {
  includes: string[];
  excludes: string[];
};

function lineHas(line: string, term: string): boolean {
  return line.toLowerCase().includes(term.toLowerCase());
}

/**
 * Parse grep box text.
 * - Plain tokens must all match (AND).
 * - `-term` tokens exclude lines that match.
 *
 * Examples:
 *   `[close] -uptime_monitor` → close lines without uptime probes
 *   `-uptime_monitor`         → everything except uptime
 *   `[close]`                 → close lines only
 */
export function parseLogGrepQuery(raw: string): LogGrepFilter {
  const q = raw.trim();
  if (!q) return { includes: [], excludes: [] };

  const includes: string[] = [];
  const excludes: string[] = [];
  for (const tok of q.split(/\s+/)) {
    if (!tok) continue;
    if (tok.startsWith("-") && tok.length > 1) {
      excludes.push(tok.slice(1));
    } else {
      includes.push(tok);
    }
  }

  return { includes, excludes };
}

export function logGrepFilterActive(filter: LogGrepFilter): boolean {
  return filter.includes.length > 0 || filter.excludes.length > 0;
}

export function filterLogLines(lines: string[], filter: LogGrepFilter): string[] {
  if (!logGrepFilterActive(filter)) return lines;
  return lines.filter((line) => {
    if (filter.includes.length > 0 && !filter.includes.every((term) => lineHas(line, term))) {
      return false;
    }
    if (filter.excludes.some((term) => lineHas(line, term))) {
      return false;
    }
    return true;
  });
}

export function logGrepFilterLabel(filter: LogGrepFilter): string {
  const parts: string[] = [];
  if (filter.includes.length > 0) {
    parts.push(filter.includes.map((t) => `“${t}”`).join(" + "));
  }
  if (filter.excludes.length > 0) {
    parts.push(`exclude ${filter.excludes.map((t) => `“${t}”`).join(", ")}`);
  }
  return parts.join("; ");
}
